import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAdvisorBgaSettings, selectSession, selectSessionAdvisor } from '@client/model/selectors';
import { fetchAdvisorBgaOrganizationsByAdvisorIdRequest } from '@client/model/advisorBgaCarrierSettingsModel';
import { SessionType } from '@shared';

/**
 * Simple hook for fetching and accessing BGA data
 */
export function useBgaData() {
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const bgaSettings = useSelector(selectAdvisorBgaSettings);

  const isAdvisor =
    session?.sessionType === SessionType.advisor || session?.sessionType === SessionType.wizard;

  // Determine if we should fetch data
  const shouldFetchData = useMemo(() => {
    return isAdvisor && advisor?.advisorId && bgaSettings.status === null && bgaSettings.data.length === 0;
  }, [isAdvisor, advisor?.advisorId, bgaSettings]);

  // Fetch BGAs when advisor is available and no data exists
  useEffect(() => {
    if (shouldFetchData) {
      dispatch(fetchAdvisorBgaOrganizationsByAdvisorIdRequest({ advisorId: advisor.advisorId }));
    }
  }, [shouldFetchData, dispatch, advisor?.advisorId]);

  // Utility functions for working with BGA data
  const isCarrierAssigned = (carrierId: number) => {
    return bgaSettings.data.some(bga => bga.assignedCarrierId === carrierId);
  };

  const getBgaForCarrier = (carrierId: number) => {
    return bgaSettings.data.find(bga => bga.assignedCarrierId === carrierId);
  };

  const getBgaById = (bgaOrganizationId: string) => {
    return bgaSettings.data.find(bga => bga.advisorOrganizationId === bgaOrganizationId);
  };

  return {
    bgas: bgaSettings.data,
    isLoading: bgaSettings.status === 'loading',
    hasError: bgaSettings.status === 'error',
    status: bgaSettings.status,
    isCarrierAssigned,
    getBgaForCarrier,
    getBgaById,
  };
}
